// src/services/quotationService.js

import {
  collection, addDoc, updateDoc, doc,
  getDocs, getDoc, query, where, serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";

// ── Quotation Status ──────────────────────────────────────────────────────────
export const QUOTATION_STATUS = {
  draft:       { label: "Draft",       color: "bg-gray-100 text-gray-600" },
  sent:        { label: "Sent",        color: "bg-blue-100 text-blue-700" },
  negotiation: { label: "Negotiation", color: "bg-amber-100 text-amber-700" },
  accepted:    { label: "Accepted",    color: "bg-green-100 text-green-700" },
  rejected:    { label: "Rejected",    color: "bg-red-100 text-red-700" },
  expired:     { label: "Expired",     color: "bg-gray-100 text-gray-400" },
};

// ── BOQ Sections ──────────────────────────────────────────────────────────────
export const BOQ_SECTIONS = {
  material:     { label: "Material Supply",      icon: "📦" },
  installation: { label: "Installation Charges", icon: "🔧" },
  civil:        { label: "Civil Work",           icon: "🏗️" },
  misc:         { label: "Miscellaneous",        icon: "📋" },
  optional:     { label: "Optional Items",       icon: "⭐" },
};

// ── Units ─────────────────────────────────────────────────────────────────────
export const BOQ_UNITS = ["Nos", "Mtr", "Kg", "LS", "TR", "Set", "Lot", "Sqft", "RFT"];

// ── Generate Quotation Number ─────────────────────────────────────────────────
const generateQuotationNo = async (company_id) => {
  const snap = await getDocs(query(
    collection(db, "quotations"),
    where("company_id", "==", company_id)
  ));
  const year = new Date().getFullYear();
  return `QT-${year}-${String(snap.size + 1).padStart(4, "0")}`;
};

// ── Create Quotation ──────────────────────────────────────────────────────────
export const createQuotation = async (data, user) => {
  const company_id = user.company_id || "";
  if (!company_id) throw new Error("company_id is required");
  const quotation_no = await generateQuotationNo(company_id);

  const ref = await addDoc(collection(db, "quotations"), {
    company_id,
    quotation_no,
    enquiry_id:       data.enquiry_id       || "",
    client_id:        data.client_id        || "",
    client_name:      data.client_name      || "",
    client_phone:     data.client_phone     || "",
    client_email:     data.client_email     || "",
    site_id:          data.site_id          || "",
    site_name:        data.site_name        || "",
    project_type:     data.project_type     || "installation",
    subject:          data.subject          || "",
    validity_days:    Number(data.validity_days || 30),
    validity_date:    data.validity_date    || "",
    payment_terms:    data.payment_terms    || "40% advance, 50% on completion, 10% retention",
    terms_conditions: data.terms_conditions || "",
    notes:            data.notes            || "",
    status:           "draft",
    revision:         0,
    revision_label:   "Rev 0",
    parent_quotation_id: "",
    items:            data.items            || [],
    subtotal:         Number(data.subtotal  || 0),
    discount:         Number(data.discount  || 0),
    tax_percent:      Number(data.tax_percent || 18),
    tax_amount:       Number(data.tax_amount || 0),
    total_amount:     Number(data.total_amount || 0),
    converted_to_project: false,
    project_id:       "",
    created_by:       user.uid || user.id   || "",
    created_by_name:  user.name             || "",
    created_at:       serverTimestamp(),
    updated_at:       serverTimestamp(),
  });

  return ref.id;
};

// ── Update Quotation ──────────────────────────────────────────────────────────
export const updateQuotation = async (quotation_id, data, company_id) => {
  if (!company_id) throw new Error("company_id is required");
  const ref  = doc(db, "quotations", quotation_id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Quotation not found");
  if (snap.data().company_id !== company_id) {
    throw new Error("Access denied — quotation belongs to a different company");
  }
  const { company_id: _ignored, ...safeData } = data; // never let caller overwrite tenant tag
  await updateDoc(ref, {
    ...safeData,
    updated_at: serverTimestamp(),
  });
};

// ── Create Revision ───────────────────────────────────────────────────────────
export const createRevision = async (originalId, user) => {
  const snap = await getDoc(doc(db, "quotations", originalId));
  if (!snap.exists()) throw new Error("Quotation not found");

  const original = snap.data();
  const company_id = user.company_id || "";
  if (!company_id) throw new Error("company_id is required");
  if (original.company_id !== company_id) {
    throw new Error("Access denied — quotation belongs to a different company");
  }

  // Root parent ID nikalo
  const rootId = original.parent_quotation_id || originalId;

  // Root ke sab revisions count karo
  const revSnap = await getDocs(query(
    collection(db, "quotations"),
    where("company_id", "==", company_id),
    where("parent_quotation_id", "==", rootId)
  ));
  const newRevision = revSnap.size + 1;

  // Root ka base number nikalo
  const rootSnap = await getDoc(doc(db, "quotations", rootId));
  const rootData  = rootSnap.exists() ? rootSnap.data() : null;
  const baseNo    = rootData ? rootData.quotation_no : original.quotation_no.replace(/-R\d+$/, "");

  const ref = await addDoc(collection(db, "quotations"), {
    ...original,
    quotation_no: `${baseNo}-R${newRevision}`,
    revision:            newRevision,
    revision_label:      `Rev ${newRevision}`,
    parent_quotation_id: rootId,
    status:              "draft",
    converted_to_project: false,
    project_id:          "",
    created_by:          user.uid || user.id || "",
    created_by_name:     user.name || "",
    created_at:          serverTimestamp(),
    updated_at:          serverTimestamp(),
  });

  // ✅ Original ko superseded mark karo
  await updateDoc(doc(db, "quotations", originalId), {
    status:     "superseded",
    updated_at: serverTimestamp(),
  });

  return ref.id;
};

// ── Fetch Quotations ──────────────────────────────────────────────────────────
export const fetchQuotations = async (company_id) => {
  const snap = await getDocs(query(
    collection(db, "quotations"),
    where("company_id", "==", company_id)
  ));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(q => q.status !== "superseded") // ✅ superseded hide karo
    .sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0));
};

// ── Fetch Single Quotation ────────────────────────────────────────────────────
export const fetchQuotation = async (quotation_id, company_id) => {
  if (!company_id) throw new Error("company_id is required");
  const snap = await getDoc(doc(db, "quotations", quotation_id));
  if (!snap.exists()) throw new Error("Quotation not found");
  const data = { id: snap.id, ...snap.data() };
  if (data.company_id !== company_id) {
    throw new Error("Access denied — quotation belongs to a different company");
  }
  return data;
};

// ── Convert to Project ────────────────────────────────────────────────────────
export const convertQuotationToProject = async (quotation_id, project_id, user) => {
  const company_id = user.company_id || "";
  if (!company_id) throw new Error("company_id is required");
  const ref  = doc(db, "quotations", quotation_id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Quotation not found");
  if (snap.data().company_id !== company_id) {
    throw new Error("Access denied — quotation belongs to a different company");
  }
  await updateDoc(ref, {
    converted_to_project: true,
    project_id,
    converted_by:      user.uid || user.id || "",
    converted_by_name: user.name || "",
    converted_at:      serverTimestamp(),
    status:            "accepted",
    updated_at:        serverTimestamp(),
  });
};

// ── Calculate Totals ──────────────────────────────────────────────────────────
export const calculateTotals = (items = [], discount = 0, tax_percent = 18) => {
  const subtotal   = items.reduce((s, i) => s + Number(i.amount || 0), 0);
  const taxable    = subtotal - Number(discount);
  const tax_amount = (taxable * Number(tax_percent)) / 100;
  const total      = taxable + tax_amount;
  return {
    subtotal:    Math.round(subtotal),
    tax_amount:  Math.round(tax_amount),
    total_amount: Math.round(total),
  };
};